import { EmptyState, EmptyStateDescription, EmptyStateIcon } from '../../../components/meeting/empty-state'
import { MeetingViewBriefBody } from './brief-body'
import { MeetingBriefGenerationPrompt, useCanGenerateBrief, useMeetingBriefAllowance } from './brief-generation'
import { LockedBrief } from './locked-brief'
import type { MeetingViewModel } from '../use-meeting-view-model'
import { FileText } from 'lucide-react'

interface MeetingViewBriefPanelProps {
  /** The open tab, passed down so only the panel on screen offers its copy. */
  isActiveTab: boolean
  model: MeetingViewModel
}

/**
 * The Brief tab: the brief, or the way to ask for one. The tab only renders
 * while a brief exists or may still land (`hasBriefTab` in body.tsx), so the
 * empty state is a waiting room, not a dead end.
 */
export function MeetingViewBriefPanel({ model, isActiveTab }: MeetingViewBriefPanelProps) {
  const canGenerate = useCanGenerateBrief(model)
  const briefs = useMeetingBriefAllowance()

  if (model.brief) {
    return <MeetingViewBriefBody model={model} className="pt-2" isActiveTab={isActiveTab} />
  }

  // Not while a brief may still load: a meeting that already has one mustn't flash the spend-a-brief wall.
  if (canGenerate && briefs && !model.briefLoading) {
    return <LockedBrief model={model} briefs={briefs} />
  }

  return (
    <div className="flex flex-col items-center gap-4 px-5 py-8">
      <EmptyState>
        <EmptyStateIcon>
          <FileText />
        </EmptyStateIcon>
        <EmptyStateDescription>
          {model.briefLoading ? 'Loading brief…' : 'No brief for this meeting yet.'}
        </EmptyStateDescription>
      </EmptyState>
      {/* Renders nothing when a brief can't be generated (meeting over, no
          calendar event), which is exactly when the tab is gone too. */}
      <div className="w-full max-w-[250px]">
        <MeetingBriefGenerationPrompt model={model} />
      </div>
    </div>
  )
}
